import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { MissingPaymentBanner } from '#client/components/MissingPaymentBanner';

export const BannerContext = createContext();
BannerContext.displayName = 'BannerContext';

const useBannerContext = () => {
  const context = useContext(BannerContext);

  if (typeof context === 'undefined') {
    throw new Error('useBannerContext must be used within a BannerContext.Provider');
  }

  return context;
};

export const BannerProvider = ({ children }) => {
  const cachedBanner = useRef(null);

  const globalBanners = [MissingPaymentBanner()];

  const activeBanner = globalBanners.find((banner) => banner !== null);
  /**
   * Result of .find method is undefined if no banner is found
   * We need to at least one banner is undefined to know that the active banner is still loading
   */
  const isActiveBannerLoading =
    activeBanner === undefined && globalBanners.some((banner) => banner === undefined);

  useEffect(() => {
    if (!isActiveBannerLoading) {
      cachedBanner.current = activeBanner;
    }
  }, [activeBanner, isActiveBannerLoading]);

  const value = useMemo(
    () => ({
      activeGlobalBanner: isActiveBannerLoading ? cachedBanner.current : activeBanner
    }),
    [activeBanner, isActiveBannerLoading]
  );

  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
};

export default useBannerContext;
